import type {
  CharacterizationApiResponse,
  RiskStatusInformation,
} from '@/domains/characterization/characterization.entity';
import type { HistorialRiesgosData } from '@/domains/characterization/characterization.entity';
import type { CharacterizationRepository } from '@/domains/characterization/characterization.repository';

const NIVEL_MAP: Record<
  number,
  { estado: boolean; nivelRiesgo: string | null }
> = {
  0: { estado: false, nivelRiesgo: null },
  1: { estado: true, nivelRiesgo: 'bajo' },
  2: { estado: true, nivelRiesgo: 'medio' },
  3: { estado: true, nivelRiesgo: 'alto' },
};

const HISTORIAL_KEY_MAP: Record<string, string> = {
  riesgo_nota: 'nota',
  riesgo_asistencia: 'asistencia',
  riesgo_beneficio: 'beneficio',
  riesgo_social: 'social',
  riesgo_otro: 'otro',
};

/**
 * Use Case para obtener datos de caracterización de estudiantes desde el repositorio
 *
 * Responsabilidades:
 * - Obtener datos de caracterización de un estudiante específico
 * - Obtener historial de riesgos del estudiante
 * - Enriquecer el response reemplazando cardRiskList con datos actuales del historial
 * - Validar que la respuesta contenga datos
 * - Actuar como intermediario entre la capa de dominio y la infraestructura
 *
 * Los datos de caracterización incluyen información socioeconómica, académica y demográfica del estudiante
 */
export class GetCharacterizationUseCase {
  private characterizationRepository: CharacterizationRepository;

  /**
   * Inicializa el UseCase con el repositorio de caracterización
   * @param characterizationRepository - Repositorio que implementa la interfaz CharacterizationRepository
   */
  constructor(characterizationRepository: CharacterizationRepository) {
    this.characterizationRepository = characterizationRepository;
  }

  /**
   * Ejecuta la obtención de datos de caracterización de un estudiante
   * @param codIncorporacionEstudiante - Código único de incorporación del estudiante
   * @throws Error si no se pueden obtener los datos de caracterización
   */
  async execute(
    codIncorporacionEstudiante: string,
  ): Promise<CharacterizationApiResponse> {
    const [characterizationResponse, historialResponse] = await Promise.all([
      this.characterizationRepository.getCharacterization(
        codIncorporacionEstudiante,
      ),
      this.characterizationRepository.getHistorialRiesgos(
        codIncorporacionEstudiante,
      ),
    ]);

    if (!characterizationResponse)
      throw new Error('Error al obtener datos de caracterización');

    if (!historialResponse)
      throw new Error('Error al obtener historial de riesgos');

    const enrichedData = {
      ...characterizationResponse.data,
      cardRiskList: {
        porcentajeGeneralRiesgo:
          characterizationResponse.data.cardRiskList.porcentajeGeneralRiesgo,
        riesgos: this.buildRiesgosFromHistorial(historialResponse.data),
      },
      cardRiskHistory: historialResponse.data,
    };

    return {
      ...characterizationResponse,
      data: enrichedData,
    };
  }

  private buildRiesgosFromHistorial(
    historial: HistorialRiesgosData,
  ): Record<string, RiskStatusInformation> {
    const riesgos: Record<string, RiskStatusInformation> = {};

    for (const [historialKey, riskKey] of Object.entries(HISTORIAL_KEY_MAP)) {
      const item = historial[historialKey as keyof HistorialRiesgosData];
      if (item && typeof item === 'object' && 'actual' in item) {
        riesgos[riskKey] = NIVEL_MAP[item.actual] ?? {
          estado: false,
          nivelRiesgo: null,
        };
      }
    }

    return riesgos;
  }
}
